import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useEffect } from "react";

// Pages
import Home from "@/pages/Home";
import CategoryHome from "@/pages/CategoryHome";
import StartInvesting from "@/pages/StartInvesting";
import DebtFree from "@/pages/DebtFree";
import BuildWealth from "@/pages/BuildWealth";
import LearnFinance from "@/pages/LearnFinance";
import PlanSave from "@/pages/PlanSave";
import Tools from "@/pages/Tools";
import CategoryQuizPage from "@/pages/CategoryQuizPage";
import Mission from "@/pages/Mission";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Courses from "@/pages/Courses";
import Seminars from "@/pages/Seminars";
// Survey page removed - quiz is now on homepage
import Admin from "@/pages/Admin";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import BookPreview from "@/pages/BookPreview";
import ETFQuiz from "@/pages/ETFQuiz";
import Products from "@/pages/Products";
import Dashboard from "@/pages/Dashboard";
import Onboarding from "@/pages/Onboarding";

// Components
import ResetPassword from "@/components/ResetPassword";
import ForgotPassword from "@/components/ForgotPassword";
import AuthCallback from "@/components/AuthCallback";

// Layout
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import AnalyticsTracker from "@/components/AnalyticsTracker";

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Layout wrapper - Navbar and Footer on ALL pages
const AppLayout = ({ children }) => {
  const location = useLocation();
  // Pages that have their own footer
  const customFooterPages = ["/", "/start-investing", "/debt-free", "/build-wealth", "/learn-finance", "/plan-save", "/tools", "/category-quiz", "/dashboard", "/onboarding"];
  const hasCustomFooter = customFooterPages.includes(location.pathname);
  
  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="flex-1">{children}</main>
      {!hasCustomFooter && <Footer />}
    </>
  );
};

function App() {
  return (
    <div className="App min-h-screen flex flex-col">
      <BrowserRouter>
        <AnalyticsTracker>
          <Routes>
            {/* Auth callback - outside layout for clean redirect */}
            <Route path="/auth/callback" element={<AuthCallback />} />
            
            {/* All other routes with layout */}
            <Route path="/*" element={
              <AppLayout>
                <Routes>
                  {/* Category Home as main landing */}
                  <Route path="/" element={<CategoryHome />} />
                  
                  {/* Category Quiz */}
                  <Route path="/category-quiz" element={<CategoryQuizPage />} />
                  
                  {/* Category Pages */}
                  <Route path="/start-investing" element={<StartInvesting />} />
                  <Route path="/debt-free" element={<DebtFree />} />
                  <Route path="/build-wealth" element={<BuildWealth />} />
                  <Route path="/learn-finance" element={<LearnFinance />} />
                  <Route path="/plan-save" element={<PlanSave />} />
                  <Route path="/tools" element={<Tools />} />
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/onboarding" element={<Onboarding />} />
                  
                  {/* Original Pages (with navbar/footer) */}
                  <Route path="/home" element={<Home />} />
                  <Route path="/products" element={<Products />} />
                  <Route path="/mission" element={<Mission />} />
                  <Route path="/blog" element={<Blog />} />
                  <Route path="/blog/:slug" element={<BlogPost />} />
                  <Route path="/courses" element={<Courses />} />
                  <Route path="/seminars" element={<Seminars />} />
                  {/* Survey route removed - redirects to homepage */}
                  <Route path="/admin" element={<Admin />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/book/:filename" element={<BookPreview />} />
                  <Route path="/etf-quiz" element={<ETFQuiz />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                </Routes>
              </AppLayout>
            } />
          </Routes>
          <Toaster position="top-right" />
        </AnalyticsTracker>
      </BrowserRouter>
    </div>
  );
}

export default App;
